"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { useLanguage } from "@/lib/language-context"
import { Check } from "lucide-react"

interface RequestSparePartDialogProps {
  trigger: React.ReactNode
}

export function RequestSparePartDialog({ trigger }: RequestSparePartDialogProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [formData, setFormData] = useState({
    productName: "",
    manufacturer: "",
    modelNumber: "",
    description: "",
    email: "",
    phone: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const { t } = useLanguage()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    
    // Simulate API request / submission
    await new Promise((resolve) => setTimeout(resolve, 1500))
    
    setSubmitting(false)
    setSubmitted(true)
    setTimeout(() => {
      setIsOpen(false)
      setSubmitted(false)
      setFormData({
        productName: "",
        manufacturer: "",
        modelNumber: "",
        description: "",
        email: "",
        phone: "",
      })
    }, 2500)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] p-6 max-h-[90vh] overflow-y-auto z-[110]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{t("request_part.title")}</DialogTitle>
          <DialogDescription>
            {t("request_part.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4 animate-in fade-in zoom-in duration-300">
            <div className="h-16 w-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-200">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold">{t("request_part.success_title")}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {t("request_part.success_desc")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid gap-2">
              <Label htmlFor="productName">{t("request_part.prod_name")}</Label>
              <Input
                id="productName"
                required
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                placeholder={t("request_part.prod_name_placeholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manufacturer">{t("request_part.manufacturer")}</Label>
              <Input
                id="manufacturer"
                required
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder={t("request_part.manufacturer_placeholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="modelNumber">{t("request_part.model")}</Label>
              <Input
                id="modelNumber"
                value={formData.modelNumber}
                onChange={(e) => setFormData({ ...formData, modelNumber: e.target.value })}
                placeholder={t("request_part.model_placeholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">{t("request_part.desc")}</Label>
              <Textarea
                id="description"
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t("request_part.desc_placeholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">{t("request_part.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t("request_part.email_placeholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone">{t("request_part.phone")}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t("request_part.phone_placeholder")}
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full mt-4"
            >
              {submitting ? t("request_part.submitting") : t("request_part.submit")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
